import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Phone } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Conversation states
const STATES = {
  INITIAL: 'INITIAL',
  AWAITING_CHOICE: 'AWAITING_CHOICE',
  RECOMMEND_NAME: 'RECOMMEND_NAME',
  RECOMMEND_PHONE: 'RECOMMEND_PHONE',
  RECOMMEND_CATEGORY: 'RECOMMEND_CATEGORY',
  SEARCH_QUERY: 'SEARCH_QUERY',
  IDLE: 'IDLE'
};

const ChatWidget = ({ isOpen, onClose, onOpen }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationState, setConversationState] = useState(STATES.INITIAL);
  const [tempProviderData, setTempProviderData] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Handle body scroll lock on mobile when chat is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('chat-open');
    } else {
      document.body.classList.remove('chat-open');
    }
    return () => document.body.classList.remove('chat-open');
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Send welcome message and menu when chat opens
      const timer = setTimeout(() => {
        addBotMessage("Welcome to BMP Yellow Pages! 👋\nYour trusted community service directory.");
        setTimeout(() => {
          addBotMessage("**Type 1** if you want to recommend a service provider to your neighbors\n\n**Type 2** if you want to search for a service provider\n\n**Type 3** to list all providers alphabetically\n\nPlease enter 1, 2, or 3 to continue.");
          setConversationState(STATES.AWAITING_CHOICE);
        }, 1000);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, conversationState]);

  const addBotMessage = (text, extras = {}) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      ...extras
    }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
  };

  const simulateTyping = async (callback, delay = 1000) => {
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, delay));
    setIsTyping(false);
    callback();
  };

  const showMainMenu = () => {
    simulateTyping(() => {
      addBotMessage("**Type 1** if you want to recommend a service provider to your neighbors\n\n**Type 2** if you want to search for a service provider\n\n**Type 3** to list all providers alphabetically\n\nPlease enter 1, 2, or 3 to continue.");
      setConversationState(STATES.AWAITING_CHOICE);
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const value = inputValue.trim();
    if (!value) return;
    
    addUserMessage(value);
    setInputValue('');

    const lowerValue = value.toLowerCase();

    // Global command: go back to main menu from anywhere
    if (value === '0' || lowerValue === 'back' || lowerValue === 'menu') {
      setTempProviderData({});
      showMainMenu();
      return;
    }

    // Handle different conversation states
    switch (conversationState) {
      case STATES.AWAITING_CHOICE:
        if (value === '1') {
          simulateTyping(() => {
            addBotMessage("Great! Let's add a new service provider.\n\nWhat is the name of the service provider?\n\n(Type 0 to go back)");
            setConversationState(STATES.RECOMMEND_NAME);
          });
        } else if (value === '2') {
          simulateTyping(() => {
            addBotMessage("Type here what you're looking for...\n\nFor e.g. Plumber, Dentist, Tennis, Tutor, Cook, Electrician, etc.\n\n(Type 0 to go back)");
            setConversationState(STATES.SEARCH_QUERY);
          });
        } else if (value === '3') {
          // List all providers alphabetically
          setIsTyping(true);
          try {
            const response = await axios.get(`${API}/providers`);
            setIsTyping(false);
            
            if (response.data.length > 0) {
              // Sort alphabetically by category, then by name
              const sortedProviders = response.data.sort((a, b) => {
                const catCompare = (a.category || '').localeCompare(b.category || '');
                if (catCompare !== 0) return catCompare;
                return (a.name || '').localeCompare(b.name || '');
              });
              
              addBotMessage(`📋 **All Service Providers (${sortedProviders.length})**`, {
                allProviders: sortedProviders
              });
            } else {
              addBotMessage("No service providers found yet.\n\nType 1 to add the first one!");
            }
          } catch (error) {
            setIsTyping(false);
            addBotMessage("Sorry, there was an error fetching providers. Please try again.");
          }
          
          setTimeout(() => showMainMenu(), 2000);
        } else if (lowerValue === 'no' || lowerValue === 'exit' || lowerValue === 'close') {
          addBotMessage("No problem! Feel free to come back anytime. 👋");
          setTimeout(() => onClose(), 1500);
        } else {
          addBotMessage("Please enter 1, 2, or 3 to continue.\n\n1 = Recommend a service provider\n2 = Search for a service provider\n3 = List all providers\n0 = Back to main menu");
        }
        break;

      case STATES.RECOMMEND_NAME:
        if (lowerValue === 'no' || lowerValue === 'exit' || lowerValue === 'cancel') {
          addBotMessage("No problem! 👋");
          setTimeout(() => showMainMenu(), 1000);
        } else {
          setTempProviderData({ name: value });
          simulateTyping(() => {
            addBotMessage(`Got it! "${value}"\n\nWhat is their phone number?`);
            setConversationState(STATES.RECOMMEND_PHONE);
          });
        }
        break;

      case STATES.RECOMMEND_PHONE:
        if (lowerValue === 'no' || lowerValue === 'exit' || lowerValue === 'cancel') {
          addBotMessage("No problem! 👋");
          setTempProviderData({});
          setTimeout(() => showMainMenu(), 1000);
        } else {
          setTempProviderData(prev => ({ ...prev, phone: value }));
          simulateTyping(() => {
            addBotMessage(`Phone: "${value}"\n\nWhat category of service do they provide?\n(e.g., Plumber, Electrician, Tutor, Cleaner)`);
            setConversationState(STATES.RECOMMEND_CATEGORY);
          });
        }
        break;

      case STATES.RECOMMEND_CATEGORY:
        if (lowerValue === 'no' || lowerValue === 'exit' || lowerValue === 'cancel') {
          addBotMessage("No problem! 👋");
          setTempProviderData({});
          setTimeout(() => showMainMenu(), 1000);
        } else {
          const finalData = { ...tempProviderData, category: value };
          setIsTyping(true);
          
          try {
            await axios.post(`${API}/providers`, finalData);
            // Get total count after adding
            const countResponse = await axios.get(`${API}/providers`);
            const totalCount = countResponse.data.length;
            setIsTyping(false);
            addBotMessage(`Thank you for your recommendation! 🎉\n\n✅ ${finalData.name}\n📞 ${finalData.phone}\n🏷️ ${finalData.category}\n\nYour neighbors will appreciate this!\n\n📊 **Total Service Providers: ${totalCount}**`);
          } catch (error) {
            setIsTyping(false);
            addBotMessage("Sorry, there was an error saving the provider. Please try again.");
          }
          
          setTempProviderData({});
          setTimeout(() => showMainMenu(), 2000);
        }
        break;

      case STATES.SEARCH_QUERY:
        if (lowerValue === 'no' || lowerValue === 'exit' || lowerValue === 'cancel') {
          addBotMessage("No problem! 👋");
          setTimeout(() => showMainMenu(), 1000);
        } else {
          setIsTyping(true);
          
          try {
            const response = await axios.post(`${API}/providers/search`, { query: value });
            setIsTyping(false);
            
            if (response.data.length > 0) {
              addBotMessage(`Found ${response.data.length} service provider(s) for "${value}":`, {
                results: response.data
              });
            } else {
              addBotMessage(`No service providers found for "${value}".`);
            }
          } catch (error) {
            setIsTyping(false);
            addBotMessage("Sorry, there was an error searching. Please try again.");
          }
          
          setTimeout(() => showMainMenu(), 2000);
        }
        break;

      default:
        showMainMenu();
        break;
    }
  };

  const getPlaceholder = () => {
    switch (conversationState) {
      case STATES.AWAITING_CHOICE:
        return "Enter 1, 2, or 3...";
      case STATES.RECOMMEND_NAME:
        return "Enter provider name...";
      case STATES.RECOMMEND_PHONE:
        return "Enter phone number...";
      case STATES.RECOMMEND_CATEGORY:
        return "Enter service category...";
      case STATES.SEARCH_QUERY:
        return "Type what you're looking for...";
      default:
        return "Type your message...";
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            data-testid="chat-launcher"
            className="chat-launcher"
            onClick={onOpen}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            <MessageCircle />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="chat-window"
            className="chat-window"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            {/* Header */}
            <div className="chat-header">
              <div className="chat-header-info">
                <div>
                  <h2 className="chat-title">BMP Yellow Pages</h2>
                  <p className="chat-subtitle">Your Community Concierge</p>
                </div>
              </div>
              <button 
                data-testid="chat-close-btn"
                className="chat-close-btn"
                onClick={onClose}
              >
                <X />
              </button>
            </div>

            {/* Messages */}
            <ScrollArea className="messages-container">
              <div className="flex flex-col gap-2">
                {messages.map((msg) => (
                  <motion.div
                    key={msg.id}
                    className={`message ${msg.type}`}
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                  >
                    <p style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ 
                      __html: msg.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                    }} />
                    
                    {/* Show search results */}
                    {msg.results && msg.results.map((provider, idx) => (
                      <div key={idx} className="provider-card">
                        <div className="provider-info">
                          <div className="provider-avatar">
                            {provider.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="provider-details">
                            <h4>{provider.name}</h4>
                            <p>{provider.category || 'Service Provider'}</p>
                          </div>
                        </div>
                        <a 
                          href={`tel:${provider.phone.replace(/[^0-9+]/g, '')}`} 
                          className="provider-phone"
                          data-testid={`call-provider-${idx}`}
                        >
                          <Phone size={16} />
                          <span>{provider.phone}</span>
                          <span className="call-btn">Call</span>
                        </a>
                      </div>
                    ))}

                    {/* Show all providers list */}
                    {msg.allProviders && (
                      <div className="providers-list-container">
                        {msg.allProviders.map((provider, idx) => (
                          <div key={idx} className="provider-list-item">
                            <div className="provider-list-category">{provider.category || 'N/A'}</div>
                            <div className="provider-list-name">{provider.name}</div>
                            <a 
                              href={provider.phone.startsWith('http') ? provider.phone : `tel:${provider.phone.replace(/[^0-9+]/g, '')}`}
                              className="provider-list-phone"
                              target={provider.phone.startsWith('http') ? '_blank' : '_self'}
                              rel="noopener noreferrer"
                            >
                              {provider.phone}
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <span className="message-time">{msg.timestamp}</span>
                  </motion.div>
                ))}

                {/* Typing Indicator */}
                {isTyping && (
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input Area */}
            <form className="input-area" onSubmit={handleSubmit}>
              <input
                ref={inputRef}
                data-testid="chat-input"
                type="text"
                className="chat-input"
                placeholder={getPlaceholder()}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <button
                data-testid="chat-send-btn"
                type="submit"
                className="send-btn"
                disabled={!inputValue.trim()}
              >
                <Send />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
